// LadderRow — one rung of an exclusive ladder. Shared by ProjectFacet and FurnishingFacet.
//
// WHY A SHARED ROW RATHER THAN TWO COPIES. Every exclusive axis in this panel needs the same
// thing: a radio-shaped control, the live label, an optional count, and "click the selected one
// to clear". Copying that twice is how two ladders end up disagreeing about what a second click
// does — and a ladder whose second click does nothing is indistinguishable from a broken filter.
//
// ⚠️ RADIO SHAPE, NOT A CHECKBOX, and the shape is the message: a flat sits on exactly ONE rung.
// A square box invites ticking two, and for an exclusive axis that produces an answer which
// confirms nothing. That is the whole reason axisOf pulls these codes out of the checkbox panel.
// ⚠️ The exact failure depends on which parameter carries the codes, and it is worth knowing
// before diagnosing one: through `p_feature_codes` an absent code reads NULL and verdicts
// 'unknown', so the whole corpus is DEMOTED TO THE SHELF (measured: 0 matched · 617 shelved) —
// not emptied. Through `p_condition_codes` the rungs are OR-ed by array overlap, so ticking two
// widens rather than contradicts. Neither is "zero rows", which is what this file claimed until
// it was measured. See FurnishingFacet.jsx for the numbers.
//
// ⚠️ THE COUNT IS CORPUS-WIDE, not view-scoped — it comes from the vocabulary's own usage figure,
// the only per-code number the panel has — AND SINCE F-088 (owner, 2026-09-09) A POSITIVE ONE IS
// NEVER PRINTED. It used to render as a plain number "never as results", and the second agent's
// audit found exactly what that caveat could not prevent: "39,662 nonstandard project beside a much
// smaller inventory" — a whole-corpus figure sitting beside a filtered view reads as the view's
// answer whatever it is called. The 2026-08-06 ruling already said the condition facet gets NO
// counts; B273 says a facet's number equals what the click delivers or there is no number. The
// `count` prop stays: it is the SUPPLY FLOOR (a rung nothing carries is hidden or disabled), which
// is a corpus question and legitimately so. Only the digit is gone. The ZERO on a disabled range
// bucket stays — it is the one corpus number that is true under every filter, and it is the
// explanation for the greying (see below).
// ⚠️ THE TAP TARGET IS MOBILE-FIRST: 44px base, collapsing to the panel's dense 24px at `md`.
// `md` is not a guess — PANEL_IS_MOBILE in IntakeTab.jsx is `matchMedia('(min-width: 768px)')`,
// the same breakpoint, so the row is comfortable in exactly the branch that renders the phone
// panel and dense in exactly the branch that renders the sidebar.
// ⚠️ IT IS `max-md:min-h-11`, NOT `min-h-11 md:min-h-0`, and the difference is not style. The
// pair declares two competing rules of equal specificity and lets SOURCE ORDER decide — and in
// dev, HMR appends regenerated utilities after the media block, so the base rule won and the
// desktop row stayed 44px. It was measured that way and would have shipped looking correct in a
// production build for the wrong reason. A single one-sided rule cannot be re-ordered into the
// wrong answer.
// MEASURED 2026-08-28, and the finding is wider than this file: the panel's existing four-state
// feature rows expose a 16px GLYPH BOX as the only click target — the label beside it is a
// separate element — so on a phone they are a third of the 44px guidance. These two ladders are
// the first rows in the panel to be right, which will make them look different from their
// neighbours until the rest follow. That is a real cost and it is deliberate: the alternative is
// to ship a new control already knowing it is hard to tap. Widening every existing row is a
// density change across the whole sidebar and belongs to the designer, not to this commit.
// ⚠️ `disabled` IS A THIRD STATE, AND IT EXISTS BECAUSE THE SUPPLY FLOOR CANNOT ANSWER A RANGE.
// The floor — hide any rung nothing carries — is right for a LIST of independent rungs: an option
// that returns nothing reads as a broken filter. It is WRONG for a CONTIGUOUS RANGE, because
// removing the ends silently redraws the range's edges: with the completion buckets truncated at
// 2028, the ladder states that nothing completes after 2028 and that no deadline has passed. Both
// are false, and the reader cannot tell a missing bucket from an absent one.
// So a range renders every bucket, and the empty ones are shown DISABLED WITH THEIR ZERO — which
// says "we know about this and nothing carries it yet" instead of either lying or offering a click
// that returns nothing. ⚠️ The zero must be VISIBLE; greying alone looks like a rendering fault.
import React from 'react'

// eslint-disable-next-line no-unused-vars -- `count` is part of the row's contract (the floor) even though the digit is no longer printed
export default function LadderRow({ code, label, count = 0, selected = false, onSelect, disabled = false }) {
  return (
    <button type="button" onClick={() => { if (!disabled) onSelect(selected ? null : code) }}
      aria-pressed={selected} disabled={disabled}
      title={disabled ? `${label} — 0` : undefined}
      className={`w-full flex items-center gap-2 text-sm py-0.5 text-left group max-md:min-h-11 ${
        disabled ? 'opacity-45 cursor-default' : ''}`}>
      <span className={`h-4 w-4 shrink-0 rounded-full border flex items-center justify-center ${
        selected ? 'border-primary bg-primary/15' : 'border-input'}`}>
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
      </span>
      <span className={`flex-1 truncate ${selected
        ? 'text-foreground font-medium'
        : disabled ? 'text-muted-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}>{label}</span>
      {/* F-088: the zero shows ONLY on a disabled range bucket, where it is the explanation for
          the greying. A positive corpus count is never printed beside a filtered view; `n` still
          decides the floor upstream. */}
      {disabled && (
        <span className="text-[11px] tabular-nums text-muted-foreground/70">0</span>
      )}
    </button>
  )
}
